import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTags, FaArrowLeft } from 'react-icons/fa';
import api from '../utils/api';
import PageTransition from '../components/ui/PageTransition';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/blogs/${id}`);
        setPost(response.data);
        setLoading(false);
      } catch (error) {
        setError('Blog post not found. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  if (loading) return <LoadingContainer>Loading blog post...</LoadingContainer>;
  if (error) return <ErrorContainer>{error}</ErrorContainer>;
  if (!post) return <ErrorContainer>Blog post not found</ErrorContainer>;

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <PageTransition>
      <BlogPostContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BackLink to="/blog">
          <FaArrowLeft /> Back to Blog
        </BackLink>

        <BlogHeader>
          <BlogTitle>{post.title}</BlogTitle>

          <MetaInfo>
            <MetaItem>
              <FaCalendarAlt /> {formatDate(post.date)}
            </MetaItem>
            <MetaItem>
              <FaUser /> {post.author}
            </MetaItem>
            <MetaItem>
              <FaTags />
              {post.tags.map((tag, index) => (
                <TagLink key={index} to={`/blog?tag=${tag}`}>{tag}</TagLink>
              ))}
            </MetaItem>
          </MetaInfo>
        </BlogHeader>

        {post.image && (
          <FeaturedImage src={post.image} alt={post.title} />
        )}

        <BlogContent dangerouslySetInnerHTML={{ __html: post.content }} />

        <TagsSection>
          {post.tags.map((tag, index) => (
            <TagPill key={index} to={`/blog?tag=${tag}`}>{tag}</TagPill>
          ))}
        </TagsSection>

        <ShareSection>
          <ShareTitle>Share this post</ShareTitle>
          <ShareButtons>
            <ShareButton href={`https://twitter.com/intent/tweet?text=${post.title}&url=${window.location.href}`} target="_blank" rel="noopener noreferrer" twitter>
              Twitter
            </ShareButton>
            <ShareButton href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" facebook>
              Facebook
            </ShareButton>
            <ShareButton href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`} target="_blank" rel="noopener noreferrer" linkedin>
              LinkedIn
            </ShareButton>
          </ShareButtons>
        </ShareSection>
      </BlogPostContainer>
    </PageTransition>
  );
};

const BlogPostContainer = styled(motion.div)`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: var(--text-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    color: var(--primary-color);
  }
`;

const BlogHeader = styled.header`
  margin-bottom: 2rem;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const TagLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  margin-right: 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const BlogContent = styled.div`
  line-height: 1.8;
  color: var(--text-color);
  font-size: 1.1rem;
  
  p, ul, ol {
    margin-bottom: 1.5rem;
  }
  
  h2, h3, h4 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }
  
  h2 {
    font-size: 1.8rem;
  }
  
  h3 {
    font-size: 1.5rem;
  }
  
  h4 {
    font-size: 1.3rem;
  }
  
  ul, ol {
    padding-left: 1.5rem;
  }
  
  blockquote {
    border-left: 4px solid var(--primary-color);
    padding-left: 1rem;
    font-style: italic;
    margin: 1.5rem 0;
    color: var(--text-secondary);
  }
  
  code {
    background-color: var(--bg-secondary);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9rem;
  }
  
  pre {
    background-color: var(--bg-secondary);
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin-bottom: 1.5rem;
    
    code {
      background-color: transparent;
      padding: 0;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1.5rem 0;
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TagsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const TagPill = styled(Link)`
  background-color: var(--bg-secondary);
  color: var(--text-color);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-2px);
  }
`;

const ShareSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
`;

const ShareTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ShareButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  background-color: ${props => {
    if (props.twitter) return '#1DA1F2';
    if (props.facebook) return '#4267B2';
    if (props.linkedin) return '#0077B5';
    return 'var(--bg-secondary)';
  }};
  color: white;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: #e74c3c;
  font-size: 1.2rem;
`;

export default BlogPost;